<template>
  <Teleport to="body">
    <Transition name="modal">
      <div
        v-if="isOpen && tire"
        class="fixed inset-0 z-50 overflow-y-auto"
        @click.self="handleClose"
      >
        <div class="fixed inset-0 bg-black bg-opacity-50 transition-opacity"></div>

        <div class="flex min-h-full items-center justify-center p-4">
          <div class="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-xl dark:border-gray-700 dark:bg-black">
            <div class="sticky top-0 z-10 flex items-center justify-between border-b border-gray-200 bg-white px-6 py-4 dark:border-gray-700 dark:bg-black">
              <div class="flex items-center">
                <div class="mr-3 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/20">
                  <svg class="h-5 w-5 text-amber-600 dark:text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </div>
                <h3 class="text-xl font-semibold text-gray-900 dark:text-white">Edit Tire Registration</h3>
              </div>

              <button
                type="button"
                @click="handleClose"
                class="text-gray-400 transition-colors duration-200 hover:text-gray-500 dark:hover:text-gray-300"
              >
                <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form @submit.prevent="handleSubmit" class="px-6 py-4">
              <div class="mb-6 flex items-start gap-3 rounded-lg border border-yellow-200 bg-yellow-50 p-4 dark:border-yellow-700 dark:bg-yellow-900/20">
                <svg class="mt-0.5 h-5 w-5 flex-shrink-0 text-yellow-600 dark:text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
                </svg>
                <div>
                  <p class="mb-1 text-sm font-semibold text-yellow-800 dark:text-yellow-300">Admin tire edit</p>
                  <p class="text-xs text-yellow-700 dark:text-yellow-400">
                    You may update the <strong>Tire Pattern</strong>, <strong>Tire Size</strong>, <strong>DOT Code</strong>, <strong>Tire Position</strong>, <strong>Shop</strong>, <strong>Code</strong>, <strong>Invoice Number</strong>, <strong>Tire Photos</strong>, and <strong>Receipt Photos</strong>. Saving changes keeps the tire's current approval status unchanged. Changes to Shop, Code, and Invoice Number apply to this tire only.
                  </p>
                </div>
              </div>

              <div class="mb-6">
                <h4 class="mb-4 text-lg font-semibold text-gray-900 dark:text-white">Customer Information</h4>
                <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div class="rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
                    <p class="mb-1 text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Customer Name</p>
                    <p class="text-base font-medium text-gray-900 dark:text-white">{{ tire.user?.first_name }} {{ tire.user?.last_name }}</p>
                  </div>
                  <div class="rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
                    <p class="mb-1 text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Email</p>
                    <p class="break-all text-base font-medium text-gray-900 dark:text-white">{{ tire.user?.email || 'N/A' }}</p>
                  </div>
                </div>
              </div>

              <div class="mb-6">
                <h4 class="mb-4 text-lg font-semibold text-gray-900 dark:text-white">Vehicle Information</h4>
                <div class="rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
                  <p class="text-base font-medium text-gray-900 dark:text-white">
                    {{ tire.vehicle?.vehicle_year }} {{ tire.vehicle?.vehicle_make }} {{ tire.vehicle?.vehicle_model }}
                    <span class="text-gray-500 dark:text-gray-400">({{ tire.vehicle?.plate_number }})</span>
                  </p>
                  <p class="mt-2 text-xs text-gray-500 dark:text-gray-400">Vehicle and brand remain read-only in admin edit.</p>
                </div>
              </div>

              <div class="mb-6">
                <h4 class="mb-4 text-lg font-semibold text-gray-900 dark:text-white">Tire Information</h4>
                <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div class="rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
                    <p class="mb-1 text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Brand</p>
                    <p class="text-base font-medium text-gray-900 dark:text-white">
                      {{ tire.brand?.brand_name || 'N/A' }}
                    </p>
                  </div>

                  <div>
                    <label for="admin-tire-position" class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Tire Position <span class="text-red-500">*</span>
                    </label>
                    <select
                      id="admin-tire-position"
                      v-model="formData.tire_position"
                      required
                      class="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-900 dark:border-gray-600 dark:bg-black dark:text-white"
                      :style="inputStyle"
                    >
                      <option value="">-- Select position --</option>
                      <option v-for="pos in tirePositionOptions" :key="pos" :value="pos">{{ pos }}</option>
                    </select>
                    <p v-if="errors.tire_position" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ errors.tire_position }}</p>
                  </div>

                  <div>
                    <label for="admin-pattern" class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Tire Pattern <span class="text-red-500">*</span>
                    </label>
                    <input
                      id="admin-pattern"
                      v-model="formData.pattern"
                      type="text"
                      required
                      class="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-900 dark:border-gray-600 dark:bg-black dark:text-white"
                      :style="inputStyle"
                    />
                    <p v-if="errors.pattern" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ errors.pattern }}</p>
                  </div>

                  <div>
                    <label for="admin-tire-size" class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Tire Size <span class="text-red-500">*</span>
                    </label>
                    <input
                      id="admin-tire-size"
                      v-model="formData.tire_size"
                      type="text"
                      required
                      class="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 font-mono text-gray-900 dark:border-gray-600 dark:bg-black dark:text-white"
                      :style="inputStyle"
                    />
                    <p v-if="errors.tire_size" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ errors.tire_size }}</p>
                  </div>

                  <div class="md:col-span-2">
                    <label for="admin-dot" class="mb-2 flex items-center justify-between text-sm font-medium text-gray-700 dark:text-gray-300">
                      <span>DOT Code <span class="text-red-500">*</span></span>
                      <button type="button" @click="showDOTHelp = !showDOTHelp" class="text-xs text-blue-500 transition-colors hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300">
                        Where is this?
                      </button>
                    </label>
                    <input
                      id="admin-dot"
                      v-model="formData.dot"
                      type="text"
                      maxlength="20"
                      required
                      class="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-900 dark:border-gray-600 dark:bg-black dark:text-white"
                      :style="inputStyle"
                    />
                    <Transition name="slide-down">
                      <div v-if="showDOTHelp" class="mt-3 rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-900/20">
                        <img src="@/assets/dot-sample.png" alt="DOT Code Location on Tire" class="w-full rounded-lg border border-gray-300 shadow-sm dark:border-gray-600" />
                        <p class="mt-3 text-xs text-blue-700 dark:text-blue-300">
                          Look for the code following "DOT" on the tire sidewall. The last 4 digits represent week and year of manufacture.
                        </p>
                      </div>
                    </Transition>
                    <p v-if="errors.dot" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ errors.dot }}</p>
                  </div>
                </div>
              </div>

              <div class="mb-6">
                <h4 class="mb-4 text-lg font-semibold text-gray-900 dark:text-white">Purchase Details</h4>
                <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label for="admin-shop" class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Shop/Dealer's Name <span class="text-red-500">*</span>
                    </label>
                    <input
                      id="admin-shop"
                      v-model="formData.shop"
                      type="text"
                      required
                      class="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-900 dark:border-gray-600 dark:bg-black dark:text-white"
                      :style="inputStyle"
                    />
                    <p v-if="errors.shop" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ errors.shop }}</p>
                  </div>
                  <div>
                    <label for="admin-code" class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Shop/Dealer's Code <span class="text-red-500">*</span>
                    </label>
                    <input
                      id="admin-code"
                      v-model="formData.code"
                      type="text"
                      required
                      class="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-900 dark:border-gray-600 dark:bg-black dark:text-white"
                      :style="inputStyle"
                    />
                    <p v-if="errors.code" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ errors.code }}</p>
                  </div>
                  <div class="md:col-span-2">
                    <label for="admin-invoice" class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Invoice Number <span class="text-red-500">*</span>
                    </label>
                    <input
                      id="admin-invoice"
                      v-model="formData.invoice_number"
                      type="text"
                      required
                      class="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-900 dark:border-gray-600 dark:bg-black dark:text-white"
                      :style="inputStyle"
                    />
                    <p v-if="errors.invoice_number" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ errors.invoice_number }}</p>
                  </div>
                </div>
              </div>

              <div class="mb-6">
                <h4 class="mb-4 text-lg font-semibold text-gray-900 dark:text-white">Tire Photos</h4>

                <div v-if="tire.tire_photos?.length" class="mb-4">
                  <p class="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">Existing Tire Photos ({{ tire.tire_photos.length }})</p>
                  <div class="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4">
                    <div v-for="photo in tire.tire_photos" :key="photo.tire_photo_id" class="relative">
                      <div :class="['relative', deletedTirePhotoIds.includes(photo.tire_photo_id) ? 'opacity-50' : '']">
                        <img :src="getPhotoUrl(photo.photo_path)" alt="Tire photo" class="h-24 w-full rounded-lg border-2 border-gray-200 object-cover dark:border-gray-600" />
                        <div v-if="deletedTirePhotoIds.includes(photo.tire_photo_id)" class="absolute inset-0 flex items-center justify-center rounded-lg bg-red-500 bg-opacity-20">
                          <span class="rounded bg-white px-2 py-1 text-xs font-semibold text-red-600 dark:bg-black dark:text-red-400">Will be deleted</span>
                        </div>
                      </div>

                      <button
                        v-if="!deletedTirePhotoIds.includes(photo.tire_photo_id)"
                        type="button"
                        @click="markTirePhotoForDeletion(photo.tire_photo_id)"
                        class="absolute -right-2 -top-2 rounded-full bg-red-500 p-1.5 shadow-lg transition-all duration-200 hover:scale-110 hover:bg-red-600"
                        title="Delete photo"
                      >
                        <svg class="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>

                      <button
                        v-else
                        type="button"
                        @click="undoDeleteTirePhoto(photo.tire_photo_id)"
                        class="absolute -right-2 -top-2 rounded-full bg-blue-500 p-1.5 shadow-lg transition-all duration-200 hover:scale-110 hover:bg-blue-600"
                        title="Undo delete"
                      >
                        <svg class="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>

                <label
                  :class="[
                    'flex h-32 w-full cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed transition-all duration-200',
                    isDraggingTirePhoto
                      ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                      : 'border-gray-300 bg-gray-50 hover:border-green-400 dark:border-gray-600 dark:bg-black/50 dark:hover:border-green-500'
                  ]"
                  @dragover.prevent="isDraggingTirePhoto = true"
                  @dragleave.prevent="isDraggingTirePhoto = false"
                  @drop.prevent="handleTirePhotoDrop"
                >
                  <div class="flex flex-col items-center justify-center pb-6 pt-5">
                    <svg class="mb-3 h-10 w-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    <p class="mb-2 text-sm text-gray-500 dark:text-gray-400"><span class="font-semibold">Add tire photos</span> or drag and drop</p>
                    <p class="text-xs text-gray-500 dark:text-gray-400">JPEG, PNG, JPG (MAX. 5MB per file)</p>
                  </div>
                  <input ref="tirePhotoInputRef" type="file" accept="image/jpeg,image/png,image/jpg" multiple class="hidden" @change="handleTirePhotoSelect" />
                </label>

                <p v-if="errors.tire_photo" class="mt-2 text-sm text-red-600 dark:text-red-400">{{ errors.tire_photo }}</p>

                <div v-if="newTirePhotos.length > 0" class="mt-4">
                  <p class="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">New photos to upload ({{ newTirePhotos.length }}):</p>
                  <div class="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4">
                    <div v-for="(photo, index) in newTirePhotos" :key="`${photo.name}-${index}`" class="group relative">
                      <img :src="getPreviewUrl(photo)" :alt="`New tire ${index + 1}`" class="h-24 w-full rounded-lg border-2 border-green-300 object-cover dark:border-green-600" />
                      <button
                        type="button"
                        @click="removeNewTirePhoto(index)"
                        class="absolute right-1 top-1 rounded-full bg-red-500 p-1 opacity-0 transition-opacity duration-200 group-hover:opacity-100 hover:bg-red-600"
                      >
                        <svg class="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div class="mb-6">
                <h4 class="mb-4 text-lg font-semibold text-gray-900 dark:text-white">Receipt Photos</h4>

                <div v-if="tire.receipt_photos?.length" class="mb-4">
                  <p class="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">Existing Receipt Photos ({{ tire.receipt_photos.length }})</p>
                  <div class="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4">
                    <div v-for="photo in tire.receipt_photos" :key="photo.receipt_photo_id" class="relative">
                      <div :class="['relative', deletedReceiptPhotoIds.includes(photo.receipt_photo_id) ? 'opacity-50' : '']">
                        <img :src="getPhotoUrl(photo.photo_path)" alt="Receipt photo" class="h-24 w-full rounded-lg border-2 border-gray-200 object-cover dark:border-gray-600" />
                        <div v-if="deletedReceiptPhotoIds.includes(photo.receipt_photo_id)" class="absolute inset-0 flex items-center justify-center rounded-lg bg-red-500 bg-opacity-20">
                          <span class="rounded bg-white px-2 py-1 text-xs font-semibold text-red-600 dark:bg-black dark:text-red-400">Will be deleted</span>
                        </div>
                      </div>

                      <button
                        v-if="!deletedReceiptPhotoIds.includes(photo.receipt_photo_id)"
                        type="button"
                        @click="markReceiptPhotoForDeletion(photo.receipt_photo_id)"
                        class="absolute -right-2 -top-2 rounded-full bg-red-500 p-1.5 shadow-lg transition-all duration-200 hover:scale-110 hover:bg-red-600"
                        title="Delete photo"
                      >
                        <svg class="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>

                      <button
                        v-else
                        type="button"
                        @click="undoDeleteReceiptPhoto(photo.receipt_photo_id)"
                        class="absolute -right-2 -top-2 rounded-full bg-blue-500 p-1.5 shadow-lg transition-all duration-200 hover:scale-110 hover:bg-blue-600"
                        title="Undo delete"
                      >
                        <svg class="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>

                <label
                  :class="[
                    'flex h-32 w-full cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed transition-all duration-200',
                    isDraggingReceiptPhoto
                      ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                      : 'border-gray-300 bg-gray-50 hover:border-green-400 dark:border-gray-600 dark:bg-black/50 dark:hover:border-green-500'
                  ]"
                  @dragover.prevent="isDraggingReceiptPhoto = true"
                  @dragleave.prevent="isDraggingReceiptPhoto = false"
                  @drop.prevent="handleReceiptPhotoDrop"
                >
                  <div class="flex flex-col items-center justify-center pb-6 pt-5">
                    <svg class="mb-3 h-10 w-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    <p class="mb-2 text-sm text-gray-500 dark:text-gray-400"><span class="font-semibold">Add receipt photos</span> or drag and drop</p>
                    <p class="text-xs text-gray-500 dark:text-gray-400">JPEG, PNG, JPG (MAX. 5MB per file)</p>
                  </div>
                  <input ref="receiptPhotoInputRef" type="file" accept="image/jpeg,image/png,image/jpg" multiple class="hidden" @change="handleReceiptPhotoSelect" />
                </label>

                <p v-if="errors.receipt_photo" class="mt-2 text-sm text-red-600 dark:text-red-400">{{ errors.receipt_photo }}</p>

                <div v-if="newReceiptPhotos.length > 0" class="mt-4">
                  <p class="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">New receipt photos to upload ({{ newReceiptPhotos.length }}):</p>
                  <div class="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4">
                    <div v-for="(photo, index) in newReceiptPhotos" :key="`${photo.name}-${index}`" class="group relative">
                      <img :src="getPreviewUrl(photo)" :alt="`New receipt ${index + 1}`" class="h-24 w-full rounded-lg border-2 border-green-300 object-cover dark:border-green-600" />
                      <button
                        type="button"
                        @click="removeNewReceiptPhoto(index)"
                        class="absolute right-1 top-1 rounded-full bg-red-500 p-1 opacity-0 transition-opacity duration-200 group-hover:opacity-100 hover:bg-red-600"
                      >
                        <svg class="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div class="sticky bottom-0 flex justify-end space-x-3 border-t border-gray-200 bg-white pt-4 dark:border-gray-700 dark:bg-black">
                <button
                  type="button"
                  @click="handleClose"
                  :disabled="loading"
                  class="rounded-lg bg-gray-200 px-4 py-2 font-medium text-gray-700 transition-colors duration-200 hover:bg-gray-300 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-black dark:text-gray-300 dark:hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  :disabled="loading || !isFormValid"
                  class="flex items-center rounded-lg bg-amber-600 px-4 py-2 font-medium text-white transition-colors duration-200 hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <svg v-if="loading" class="-ml-1 mr-2 h-4 w-4 animate-spin text-white" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {{ loading ? 'Updating...' : 'Update Tire' }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useToast } from 'vue-toastification';
import { useBrand } from '@/composables/brand/useBrand';
import type { Tire } from '@/types/tire';

const props = defineProps<{
  isOpen: boolean;
  tire: Tire | null;
  loading?: boolean;
}>();

const emit = defineEmits<{
  close: [];
  save: [data: { pattern: string; tire_size: string; dot: string; tire_position: string; shop: string; code: string; invoice_number: string }, newTirePhotos: File[], deletedTirePhotoIds: number[], newReceiptPhotos: File[], deletedReceiptPhotoIds: number[]];
}>();

const toast = useToast();
const { brandColors } = useBrand();

const formData = ref({
  pattern: '',
  tire_size: '',
  dot: '',
  tire_position: '',
  shop: '',
  code: '',
  invoice_number: '',
});

const newTirePhotos = ref<File[]>([]);
const deletedTirePhotoIds = ref<number[]>([]);
const tirePhotoInputRef = ref<HTMLInputElement | null>(null);
const isDraggingTirePhoto = ref(false);
const newReceiptPhotos = ref<File[]>([]);
const deletedReceiptPhotoIds = ref<number[]>([]);
const receiptPhotoInputRef = ref<HTMLInputElement | null>(null);
const isDraggingReceiptPhoto = ref(false);
const errors = ref<Record<string, string>>({});
const showDOTHelp = ref(false);
const tirePositionOptions = ['Front Left', 'Front Right', 'Rear Left', 'Rear Right', 'Spare'];

const inputStyle = computed(() => ({
  borderColor: brandColors.value.primary,
  '--tw-ring-color': brandColors.value.primary,
  outline: 'none',
}));

const isFormValid = computed(() => {
  const currentTirePhotoCount = props.tire?.tire_photos?.length || 0;
  const remainingTirePhotos = currentTirePhotoCount - deletedTirePhotoIds.value.length + newTirePhotos.value.length;

  const currentReceiptPhotoCount = props.tire?.receipt_photos?.length || 0;
  const remainingReceiptPhotos = currentReceiptPhotoCount - deletedReceiptPhotoIds.value.length + newReceiptPhotos.value.length;

  return remainingTirePhotos >= 1 && remainingReceiptPhotos >= 1;
});

watch(
  () => props.tire,
  (newTire) => {
    if (!newTire) {
      return;
    }

    formData.value = {
      pattern: newTire.pattern,
      tire_size: newTire.tire_size,
      dot: newTire.dot,
      tire_position: newTire.tire_position || '',
      shop: newTire.shop || '',
      code: newTire.code || '',
      invoice_number: newTire.invoice_number || '',
    };
    newTirePhotos.value = [];
    deletedTirePhotoIds.value = [];
    newReceiptPhotos.value = [];
    deletedReceiptPhotoIds.value = [];
    errors.value = {};
    showDOTHelp.value = false;
  },
  { immediate: true }
);

function getPhotoUrl(path: string): string {
 const baseUrl = process.env.VUE_APP_API_URL || 'http://localhost:8000'; // live url here
 // const baseUrl = process.env.VUE_APP_API_URL?.replace('/api', '') || 'http://localhost:8000';
  return `${baseUrl}/storage/${path}`;
}

function getPreviewUrl(file: File): string {
  return URL.createObjectURL(file);
}

function handleTirePhotoDrop(event: DragEvent) {
  isDraggingTirePhoto.value = false;

  const files = Array.from(event.dataTransfer?.files || []).filter((file) =>
    ['image/jpeg', 'image/png', 'image/jpg'].includes(file.type)
  );

  if (files.length > 0) {
    processTirePhotos(files);
  }
}

function handleTirePhotoSelect(event: Event) {
  const target = event.target as HTMLInputElement;

  if (target.files) {
    processTirePhotos(Array.from(target.files));
    target.value = '';
  }
}

function processTirePhotos(files: File[]) {
  const invalidFiles = files.filter((file) => file.size > 5 * 1024 * 1024);

  if (invalidFiles.length > 0) {
    errors.value.tire_photo = 'Some files exceed 5MB limit';
    return;
  }

  errors.value.tire_photo = '';
  newTirePhotos.value.push(...files);
}

function removeNewTirePhoto(index: number) {
  newTirePhotos.value.splice(index, 1);
}

function markTirePhotoForDeletion(photoId: number) {
  if (!deletedTirePhotoIds.value.includes(photoId)) {
    deletedTirePhotoIds.value.push(photoId);
  }
}

function undoDeleteTirePhoto(photoId: number) {
  const index = deletedTirePhotoIds.value.indexOf(photoId);

  if (index > -1) {
    deletedTirePhotoIds.value.splice(index, 1);
  }
}

function handleReceiptPhotoDrop(event: DragEvent) {
  isDraggingReceiptPhoto.value = false;

  const files = Array.from(event.dataTransfer?.files || []).filter((file) =>
    ['image/jpeg', 'image/png', 'image/jpg'].includes(file.type)
  );

  if (files.length > 0) {
    processReceiptPhotos(files);
  }
}

function handleReceiptPhotoSelect(event: Event) {
  const target = event.target as HTMLInputElement;

  if (target.files) {
    processReceiptPhotos(Array.from(target.files));
    target.value = '';
  }
}

function processReceiptPhotos(files: File[]) {
  const invalidFiles = files.filter((file) => file.size > 5 * 1024 * 1024);

  if (invalidFiles.length > 0) {
    errors.value.receipt_photo = 'Some files exceed 5MB limit';
    return;
  }

  errors.value.receipt_photo = '';
  newReceiptPhotos.value.push(...files);
}

function removeNewReceiptPhoto(index: number) {
  newReceiptPhotos.value.splice(index, 1);
}

function markReceiptPhotoForDeletion(photoId: number) {
  if (!deletedReceiptPhotoIds.value.includes(photoId)) {
    deletedReceiptPhotoIds.value.push(photoId);
  }
}

function undoDeleteReceiptPhoto(photoId: number) {
  const index = deletedReceiptPhotoIds.value.indexOf(photoId);

  if (index > -1) {
    deletedReceiptPhotoIds.value.splice(index, 1);
  }
}

function handleSubmit() {
  errors.value = {};

  if (!formData.value.pattern.trim()) {
    errors.value.pattern = 'Pattern is required';
  }

  if (!formData.value.tire_size.trim()) {
    errors.value.tire_size = 'Tire size is required';
  }

  if (!formData.value.dot.trim()) {
    errors.value.dot = 'DOT code is required';
  }

  if (!formData.value.tire_position) {
    errors.value.tire_position = 'Tire position is required';
  }

  if (!formData.value.shop.trim()) {
    errors.value.shop = 'Shop/Dealer name is required';
  }

  if (!formData.value.code.trim()) {
    errors.value.code = 'Shop/Dealer code is required';
  }

  if (!formData.value.invoice_number.trim()) {
    errors.value.invoice_number = 'Invoice number is required';
  }

  const currentTirePhotoCount = props.tire?.tire_photos?.length || 0;
  const remainingTirePhotos = currentTirePhotoCount - deletedTirePhotoIds.value.length + newTirePhotos.value.length;

  if (remainingTirePhotos < 1) {
    errors.value.tire_photo = 'At least one tire photo is required';
  }

  const currentReceiptPhotoCount = props.tire?.receipt_photos?.length || 0;
  const remainingReceiptPhotos = currentReceiptPhotoCount - deletedReceiptPhotoIds.value.length + newReceiptPhotos.value.length;

  if (remainingReceiptPhotos < 1) {
    errors.value.receipt_photo = 'At least one receipt photo is required';
  }

  if (Object.keys(errors.value).filter((key) => errors.value[key]).length > 0) {
    toast.error('Please fix the errors before submitting');
    return;
  }

  emit('save', { ...formData.value }, newTirePhotos.value, deletedTirePhotoIds.value, newReceiptPhotos.value, deletedReceiptPhotoIds.value);
}

function handleClose() {
  if (!props.loading) {
    emit('close');
  }
}
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-active > div > div,
.modal-leave-active > div > div {
  transition: transform 0.3s ease;
}

.modal-enter-from > div > div,
.modal-leave-to > div > div {
  transform: scale(0.95);
}

.slide-down-enter-active,
.slide-down-leave-active {
  max-height: 500px;
  overflow: hidden;
  transition: all 0.3s ease;
}

.slide-down-enter-from,
.slide-down-leave-to {
  max-height: 0;
  opacity: 0;
  transform: translateY(-10px);
}
</style>